import { CommonModule, NgClass, NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    inject,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
    IsActiveMatchOptions,
    Router,
    RouterLink,
    RouterLinkActive,
} from '@angular/router';
import { IconsModule } from '@core/utils/icons.module';
import { FuseNavigationService } from '@fuse/components/navigation/navigation.service';
import { FuseNavigationItem } from '@fuse/components/navigation/navigation.types';
import { FuseVerticalNavigationComponent } from '@fuse/components/navigation/vertical/vertical.component';
import { FuseUtilsService } from '@fuse/services/utils/utils.service';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'fuse-vertical-navigation-basic-item',
    templateUrl: './basic.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        NgClass,
        RouterLink,
        RouterLinkActive,
        MatTooltipModule,
        NgTemplateOutlet,
        MatIconModule,
        IconsModule,
        CommonModule
    ],
})
export class FuseVerticalNavigationBasicItemComponent
    implements OnInit, OnDestroy {
    private _changeDetectorRef = inject(ChangeDetectorRef);
    private _fuseNavigationService = inject(FuseNavigationService);
    private _fuseUtilsService = inject(FuseUtilsService);
    _router = inject(Router);

    @Input() item: FuseNavigationItem;
    @Input() name: string;

    // Set the equivalent of {exact: false} as default for active match options.
    // We are not assigning the item.isActiveMatchOptions directly to the
    // [routerLinkActiveOptions] because if it's "undefined" initially, the router
    // will throw an error and stop working.
    isActiveMatchOptions: IsActiveMatchOptions =
        this._fuseUtilsService.subsetMatchOptions;

    private _fuseVerticalNavigationComponent: FuseVerticalNavigationComponent;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Set the "isActiveMatchOptions" either from item's
        // "isActiveMatchOptions" or the equivalent form of
        // item's "exactMatch" option
        this.isActiveMatchOptions =
            this.item.isActiveMatchOptions ?? this.item.exactMatch
                ? this._fuseUtilsService.exactMatchOptions
                : this._fuseUtilsService.subsetMatchOptions;

        // Get the parent navigation component
        this._fuseVerticalNavigationComponent =
            this._fuseNavigationService.getComponent(this.name);

        // Mark for check
        this._changeDetectorRef.markForCheck();

        // Subscribe to onRefreshed on the navigation component
        this._fuseVerticalNavigationComponent.onRefreshed
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(() => {
                // Mark for check
                this._changeDetectorRef.markForCheck();
            });

        this._router.events.pipe(takeUntil(this._unsubscribeAll)).subscribe(_ => {
            this._changeDetectorRef.detectChanges()
        })

        if (this.item?.showFunction) {
            let hiddenFn = this.item?.hidden ?? (i => false)
            this.item.hidden = i => {
                let hiddenResult = hiddenFn(i)
                let showFunctionResult = this.item.showFunction(this._router.url)
                let result = hiddenResult || !showFunctionResult
                // console.log(i)
                // console.log(result, hiddenResult, showFunctionResult, this._router.url)
                return result
            }
        }
        for (let c of (this.item?.children || [])) {
            c.isChild = true
        }
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }






    /**
     * Track by function for ngFor loops
     *
     * @param index
     * @param item
     */
    trackByFn(index: number, item: any): any {
        return item.id || index;
    }

    computedLink(item: FuseNavigationItem): string {
        if (!item?.link) return null
        let hiddenFn = item?.hidden ?? (i => false)
        if (hiddenFn(item)) return null
        const subLinks = item.link.split("/")
        const currentSubLinks = this._router.url.split("/")
        if (subLinks.length != currentSubLinks.length) {
            //console.error("computedLink problem", subLinks, currentSubLinks, item)
            return item.link
        }
        let result = []
        for (let i = 0; i < subLinks.length; i++) {
            result.push(subLinks[i].startsWith(":") ? currentSubLinks[i] : subLinks[i])
        }
        return result.join("/")
    }

    shoudBeActive(item: FuseNavigationItem): boolean {
        if (!item?.link) return false
        const subLinks = item.link.split("/")
        const firstParamIndex = subLinks?.findIndex(i => i.startsWith(":"))
        let result = this._router.url.startsWith(firstParamIndex == -1 || firstParamIndex == null ? item?.link : subLinks.slice(0, firstParamIndex).join("/")) || (item.children?.find(c => this.shoudBeActive(c)) != null)
        //console.log(item, firstParamIndex, subLinks.slice(0, firstParamIndex).join("/"), item.children?.find(c => this.shoudBeActive(c)) != null, result)
        return result
    }
}
